import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BRAND } from '../../theme/palette';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Enter a valid email').required('Email is required'),
  phone: Yup.string().required('Phone is required'),
  message: Yup.string(),
});

// MVP skeleton: no backend exists yet. Submission just logs the payload and
// shows a success state. TODO: wire to a real endpoint once the lead-capture
// backend is built (see implementation.md "Out of scope" section).
export default function CtaFormDialog({ open, onClose, title }) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const formik = useFormik({
    initialValues: { name: '', email: '', phone: '', message: '' },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setError('');
      try {
        const res = await fetch('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...values, source: title }),
        });
        if (!res.ok) throw new Error('Server error');
        setSubmitted(true);
      } catch {
        setError('Something went wrong. Please try again or call us directly.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    setSubmitted(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {submitted ? (
            <Alert severity="success">
              Thanks — we received your information and someone from Cherry Creek Mortgage will be in touch.
            </Alert>
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Name"
                name="name"
                fullWidth
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
              <TextField
                label="Email"
                name="email"
                fullWidth
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
              <TextField
                label="Phone"
                name="phone"
                fullWidth
                value={formik.values.phone}
                onChange={formik.handleChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
              />
              <TextField
                label="Message"
                name="message"
                fullWidth
                multiline
                minRows={3}
                value={formik.values.message}
                onChange={formik.handleChange}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {submitted ? (
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{ bgcolor: BRAND.ctaGold, color: '#1c1e2a', fontWeight: 700, '&:hover': { bgcolor: BRAND.ctaGoldHover } }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                sx={{ bgcolor: BRAND.ctaGold, color: '#1c1e2a', fontWeight: 700, '&:hover': { bgcolor: BRAND.ctaGoldHover } }}
              >
                Submit
              </Button>
            </>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
